import React, { useState, useMemo } from 'react';
import './RoyalGold.css';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const RoyalGold = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="rg-root">
            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-royal-gold.pdf` })} />

            <div className="rg-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <div key={`${page.id || page.name}-${index}`} className="rg-page pt-page active">
                            <div className="rg-border-outer">
                                <div className="rg-border-inner">
                                    {showHeader && (
                                        <header className="rg-header">
                                            <div className="rg-ornament top">✥</div>
                                            <h1 className="rg-name">{restaurant.name}</h1>
                                            <div className="rg-meta">{restaurant.address} {restaurant.phoneNumber && ` • ${restaurant.phoneNumber}`}</div>
                                            <div className="rg-ornament bottom">✥</div>
                                        </header>
                                    )}

                                    <div className="rg-body">
                                        {showMenuTitle && (
                                            <h2 className="rg-menu-title">{page.name}</h2>
                                        )}
                                        {categories.map(category => (
                                            <div key={category.id} className="rg-category">
                                                <h3 className="rg-category-title">
                                                    <span>{category.name}</span>
                                                </h3>
                                                <div className="rg-items">
                                                    {(category.items || []).map(item => (
                                                        <div key={item.id} className="rg-item">
                                                            <div className="rg-item-header">
                                                                <div className="rg-item-name-group">
                                                                    {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                        <span className={`rg-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                            <span className="rg-veg-mark"></span>
                                                                        </span>
                                                                    )}
                                                                    <span className="rg-item-name">{item.name}</span>
                                                                </div>
                                                                <span className="rg-item-dots"></span>
                                                                <span className="rg-item-price">{item.price ? `₹${item.price.toFixed(0)}` : ''}</span>
                                                            </div>
                                                            {item.description && <p className="rg-item-desc">{item.description}</p>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <footer className="rg-footer">
                                        <div className="rg-footer-text">{restaurant.establishedYear ? `Established ${restaurant.establishedYear}` : restaurant.footerText || ''}</div>
                                    </footer>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RoyalGold;
